import { SmgElement } from '../core/smg-element';
import { each, html, when, type SafeHtml } from '../core/safe-html';
import { confirmDialog } from '../services/confirm-service';
import { showToast } from '../services/toast-service';
import { wg, type WgCreated, type WgUser } from '../services/wg-service';
import qrcode from 'qrcode-generator';

/**
 * WireGuard erişim profilleri (admin): ekipteki operatörler için VPN conf
 * üretimi. Private key sunucuda SAKLANMAZ — conf yalnız oluşturma anında
 * gösterilir/indirilir; pencere kapanınca bir daha alınamaz (yeniden üretmek
 * gerekir: sil + yeni profil).
 */
class WgView extends SmgElement {
  private users: WgUser[] = [];
  private loading = true;
  private nameDraft = '';
  private creating = false;
  /** Görüntülenen conf (yeni oluşturma ya da listeden açma) — diyalog açıkken dolu */
  private created: WgCreated | null = null;
  private createdIsNew = false;

  protected override onConnected(): void {
    this.onAct('click', {
      create: () => void this.create(),
      closeCreated: () => {
        this.created = null;
        this.render();
      },
      noop: (_el, ev) => ev.stopPropagation(),
      download: () => this.download(),
      copyConf: () => void this.copyConf(),
      del: (el) => void this.del(el.dataset['name'] ?? ''),
      show: (el) => void this.show(el.dataset['name'] ?? ''),
    });
    this.onAct('input', {
      nameDraft: (el) => (this.nameDraft = (el as HTMLInputElement).value),
    });
    this.onAct('keydown', {
      nameDraft: (_el, ev) => {
        if ((ev as KeyboardEvent).key === 'Enter') void this.create();
      },
    });
    void this.load();
  }

  private async load(): Promise<void> {
    try {
      this.users = await wg.list();
    } catch {
      showToast('WireGuard listesi alınamadı', 'warning');
    }
    this.loading = false;
    this.render();
  }

  private async create(): Promise<void> {
    const name = this.nameDraft.trim();
    if (!name || this.creating) return;
    if (!/^[a-zA-Z0-9_-]{1,32}$/.test(name)) {
      showToast('İsim yalnızca harf/rakam/-/_ olabilir', 'warning');
      return;
    }
    this.creating = true;
    this.render();
    try {
      this.created = await wg.create(name);
      this.createdIsNew = true;
      this.nameDraft = '';
      await this.load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Profil oluşturulamadı', 'warning');
    }
    this.creating = false;
    this.render();
  }

  private async show(name: string): Promise<void> {
    if (!name) return;
    try {
      this.created = await wg.getConf(name);
      this.createdIsNew = false;
      this.render();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Conf alınamadı', 'warning');
    }
  }

  private download(): void {
    if (!this.created) return;
    const blob = new Blob([this.created.conf], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${this.created.name}.conf`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  private async copyConf(): Promise<void> {
    if (!this.created) return;
    try {
      await navigator.clipboard.writeText(this.created.conf);
      showToast('Conf panoya kopyalandı');
    } catch {
      showToast('Kopyalanamadı — metni elle seçin', 'warning');
    }
  }

  private async del(name: string): Promise<void> {
    if (!name) return;
    const ok = await confirmDialog({
      title: 'VPN profilini sil',
      message: `"${name}" profili silinsin mi? Bu kişinin WireGuard erişimi anında kesilir; geri almak için yeni profil üretmek gerekir.`,
      tone: 'danger',
      okLabel: 'Sil',
    });
    if (!ok) return;
    try {
      await wg.remove(name);
      showToast(`${name} silindi`);
      await this.load();
    } catch {
      showToast('Silinemedi', 'warning');
    }
  }

  /** QR: canvas'a modül modül çiz (harici görsel yok, tema bağımsız net karo). */
  private drawQr(): void {
    const canvas = this.qs<HTMLCanvasElement>('canvas[data-qr]');
    if (!canvas || !this.created) return;
    const qr = qrcode(0, 'M');
    qr.addData(this.created.conf);
    qr.make();
    const n = qr.getModuleCount();
    const scale = Math.max(2, Math.floor(232 / n));
    const size = n * scale;
    canvas.width = size + 16;
    canvas.height = size + 16;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#000000';
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        if (qr.isDark(r, c)) ctx.fillRect(8 + c * scale, 8 + r * scale, scale, scale);
      }
    }
  }

  protected override render(): void {
    super.render();
    this.drawQr();
  }

  protected override template(): SafeHtml {
    return html`
      <div class="page">
        <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:16px">
          <h1 style="font:600 22px var(--vx-font-sans);margin:0">WireGuard erişimi</h1>
          <span class="chip" style="background:var(--vx-chip-bg);color:var(--vx-chip-fg)">${this.users.length}</span>
        </div>

        <div class="card" style="padding:16px;margin-bottom:14px">
          <label class="lbl">Yeni VPN profili (ekip üyesi / bilgisayar başına bir tane)</label>
          <div style="display:flex;gap:9px;flex-wrap:wrap">
            <input class="inp" style="flex:1 1 220px" data-act="nameDraft" placeholder="ör. ahmet-laptop"
                   value="${this.nameDraft}" ${this.creating ? 'disabled' : ''}>
            <button class="btn pri" data-act="create" ${this.creating ? 'disabled' : ''}>
              <i class="ti ${this.creating ? 'ti-loader-2 spin' : 'ti-plus'}"></i>Oluştur
            </button>
          </div>
          <p style="font:400 12px/1.5 var(--vx-font-sans);color:var(--vx-text-3);margin:9px 0 0">
            Conf sunucuda saklanır (yalnız root okur) — kaybolursa listeden yeniden
            indirilebilir ya da QR gösterilebilir. Erişim alanı: 10.77.0.0/16 (filo tüneli).
          </p>
        </div>

        <div class="card" style="padding:6px 16px">
          ${when(this.loading, () => html`<div style="padding:20px;color:var(--vx-text-3)">Yükleniyor…</div>`)}
          ${when(!this.loading && !this.users.length, () => html`
            <div style="padding:20px;font:400 13px var(--vx-font-sans);color:var(--vx-text-3)">Henüz VPN profili yok.</div>`)}
          ${each(this.users, (u) => this.row(u))}
        </div>

        ${when(this.created, () => this.createdDialog(this.created!))}
      </div>`;
  }

  private row(u: WgUser): SafeHtml {
    const hs = u.handshakeAgeSec;
    const online = hs !== null && hs < 180;
    const hsText = hs === null ? 'hiç bağlanmadı' : online ? 'bağlı' : `${Math.floor(hs / 60)} dk önce`;
    const created = new Date(u.createdSec * 1000).toLocaleDateString('tr-TR');
    return html`
      <div style="display:flex;align-items:center;gap:12px;padding:12px 2px;border-top:1px solid var(--vx-border)">
        <span style="width:34px;height:34px;border-radius:9px;background:var(--vx-chip-bg);display:flex;align-items:center;justify-content:center;flex:none">
          <i class="ti ti-shield-lock" style="font-size:17px;color:var(--vx-text-2)"></i>
        </span>
        <div style="flex:1;min-width:0">
          <div style="font:600 13.5px var(--vx-font-sans)">${u.name}</div>
          <div class="mono" style="font-size:11.5px;color:var(--vx-text-3)">${u.wgIp} · ${created}</div>
        </div>
        <span class="chip" style="background:${online ? 'var(--vx-success-bg)' : 'var(--vx-chip-bg)'};color:${online ? 'var(--vx-success-text)' : 'var(--vx-chip-fg)'}">
          <i class="ti ${online ? 'ti-plug-connected' : 'ti-plug-connected-x'}" style="font-size:12px"></i>${hsText}
        </span>
        ${when(u.hasConf, () => html`
          <button class="btn sec sm" data-act="show" data-name="${u.name}" title="Conf indir / QR göster"><i class="ti ti-qrcode"></i>Conf</button>`)}
        <button class="btn dngo sm" data-act="del" data-name="${u.name}" title="Profili sil"><i class="ti ti-trash"></i></button>
      </div>`;
  }

  private createdDialog(c: WgCreated): SafeHtml {
    return html`
      <div class="ovl menu" style="animation:fadeIn .15s">
        <div class="sheet wide" data-act="noop" style="max-height:92vh;overflow:auto">
          <div style="padding:20px 20px 0;display:flex;align-items:center;gap:11px">
            <span style="width:40px;height:40px;border-radius:50%;background:var(--vx-success-bg);display:flex;align-items:center;justify-content:center;flex:none">
              <i class="ti ti-shield-check" style="font-size:22px;color:var(--vx-success)"></i>
            </span>
            <span style="font:600 17px var(--vx-font-sans)">${c.name} — ${c.wgIp}</span>
          </div>
          <div style="padding:14px 20px 6px">
            ${when(this.createdIsNew, () => html`
              <p style="font:500 12.5px/1.5 var(--vx-font-sans);color:var(--vx-success-text);background:var(--vx-success-bg);padding:9px 11px;border-radius:var(--vx-radius-md);margin:0 0 12px">
                <i class="ti ti-check" style="font-size:13px"></i>
                Profil oluşturuldu. Conf sunucuda saklı — sonradan da listeden indirebilirsiniz.
              </p>`)}
            <div style="display:flex;gap:16px;flex-wrap:wrap;align-items:flex-start">
              <div style="flex:1 1 300px;min-width:0">
                <label class="lbl">Bilgisayar: dosyayı indir, WireGuard'a import et</label>
                <pre class="mono" style="margin:0 0 10px;padding:10px 12px;background:var(--vx-term-bg);color:var(--vx-term-text);border-radius:var(--vx-radius-md);font-size:11px;line-height:1.5;white-space:pre-wrap;word-break:break-all;max-height:220px;overflow:auto">${c.conf}</pre>
                <div style="display:flex;gap:8px;flex-wrap:wrap">
                  <button class="btn pri sm" data-act="download"><i class="ti ti-download"></i>${c.name}.conf indir</button>
                  <button class="btn sec sm" data-act="copyConf"><i class="ti ti-copy"></i>Kopyala</button>
                </div>
              </div>
              <div style="flex:0 0 auto;text-align:center">
                <label class="lbl">Telefon: WireGuard → QR'dan içe aktar</label>
                <canvas data-qr style="border-radius:8px;border:1px solid var(--vx-border);max-width:248px"></canvas>
              </div>
            </div>
          </div>
          <div style="display:flex;justify-content:flex-end;padding:14px 20px 20px">
            <button class="btn sec" data-act="closeCreated">Kapat</button>
          </div>
        </div>
      </div>`;
  }
}

customElements.define('smg-wg-view', WgView);
